import {
  canReadManagementWorkspace,
  skipAccessGuard,
  type CurrentAuthorization,
} from '../api/core/authorization';
import { type TokenSource } from '../api/http/auth';
import { ApiError } from '../api/http/errors';
import { safeReturnTo, type OperatorConfig } from './config';
import { encodeRelayState } from './relay';

export const PKCE_STORAGE_KEY = 'core-admin.oauth.transaction';
/** refresh 토큰 보관 키 — 새로고침·새 탭·새 창이 같은 로그인을 이어 받도록 persistentStorage에 둔다. */
export const REFRESH_STORAGE_KEY = 'core-admin.oauth.refresh';
/** access 토큰 만료 전에 미리 갱신하는 시점(수명 대비 비율). */
const RENEW_AT = 0.8;
const RENEW_RETRY_MS = 30_000;
const TRANSACTION_TTL = 5 * 60_000;
type Status =
  | 'not-configured'
  | 'unauthenticated'
  | 'authenticating'
  | 'checking'
  | 'authorized'
  | 'forbidden'
  | 'service-failure';
export interface OperatorSnapshot {
  status: Status;
  generation: number;
  authorization: CurrentAuthorization | null;
  deniedDomains: string[];
  reason?:
    | 'expired'
    | 'failed-refresh'
    | 'invalid-callback'
    | 'logout'
    | 'login-start-failed'
    | 'login-cancelled';
}
interface Transaction {
  state: string;
  verifier: string;
  redirectUri: string;
  returnTo: string;
  expiresAt: number;
}
interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
}
interface SessionOptions {
  config: OperatorConfig | null;
  fetch: typeof fetch;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  now?: () => number;
  clearOperationalCache: () => void;
  clearDomainOperationalCache?: (domain: string, generation: number) => void;
  loadAuthorization: (accessToken: string, signal: AbortSignal) => Promise<CurrentAuthorization>;
  /**
   * 이 토큰이 누구의 것인지(코어 users/me의 user_id)를 읽는다. 권한 응답에는 신원이 없어 같은 권한의 다른 계정을 구분할 수 없으므로,
   * 토큰을 바꿀 때마다 이 값이 로그인 때와 같은지 확인한 뒤에만 화면·cache·초안을 이어 간다.
   */
  loadIdentity: (accessToken: string, signal: AbortSignal) => Promise<string>;
  /**
   * 탭·창·새로고침을 넘어 유지하는 저장소(localStorage) — refresh 토큰만 둔다. access 토큰은 계속 메모리에만 둔다.
   * 없으면 refresh 토큰도 메모리에만 두며, 새로고침하면 다시 로그인한다.
   */
  persistentStorage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** 탭 사이 refresh 직렬화(Web Locks). refresh 토큰이 회전돼도 두 탭이 같은 토큰을 동시에 쓰지 않게 한다. */
  withRefreshLock?: <T>(task: () => Promise<T>) => Promise<T>;
  /** 다른 탭의 persistentStorage 변경 알림 — 다른 탭 로그아웃을 이 탭에 전파한다. 해제 함수를 돌려준다. */
  subscribeStorage?: (
    listener: (key: string | null, newValue: string | null) => void,
  ) => () => void;
  /** 로그인 직후 한 번 — 인증 서버 신원을 코어 회원에 연결한다(첫 로그인이면 가입 처리). 실패하면 로그인 실패다. */
  resolveMember?: (accessToken: string, signal: AbortSignal) => Promise<void>;
}
/** 인증 서버가 토큰 자체를 거부했다(400/401/403) — 네트워크 단절·5xx 같은 일시 실패와 구분한다. */
class TokenRejectedError extends Error {}
const random = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};
const challenge = async (verifier: string) => {
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)),
  );
  return btoa(String.fromCharCode(...digest))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/**
 * 운영자 세션. access 토큰은 이 closure에서만 보관하며 snapshot·URL·storage에 넣지 않는다.
 * refresh 토큰은 persistentStorage가 있을 때만 거기에 둔다 — 새로고침·새 탭·새 창이 로그인을 이어 받는 유일한 경로다.
 */
export function createOperatorSession(options: SessionOptions) {
  const { config, storage } = options;
  const now = options.now ?? Date.now;
  let snapshot: OperatorSnapshot = {
    status: config ? 'unauthenticated' : 'not-configured',
    generation: 0,
    authorization: null,
    deniedDomains: [],
  };
  let access: string | null = null;
  /** 지금 세션의 계정(users/me user_id). 확인하지 못했으면 null — 그러면 신원을 보증할 수 없으므로 조용한 경로를 쓰지 않는다. */
  let identity: string | null = null;
  let refreshToken: string | null = null;
  let accessExpiresAt = 0;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let renewTimer: ReturnType<typeof setTimeout> | undefined;
  let restoreFlight: Promise<boolean> | undefined;
  let controller = new AbortController();
  let callback: { identity: string; promise: Promise<string> } | undefined;
  let loginFlight: Promise<string> | undefined;
  let authorizationFlight: Promise<void> | undefined;
  /** 진행 중인 권한 확인이 어느 access 토큰으로 시작됐는지. 다른 토큰으로는 합류하지 않는다. */
  let authorizationFlightToken: string | null = null;
  let refreshFlight: { generation: number; promise: Promise<boolean> } | undefined;
  let readRecoveryPending = new Set<string>();
  const readRecoveryCandidates = new Set<string>();
  let readRecoveryActive = false;
  const listeners = new Set<() => void>();
  const publish = (patch: Partial<OperatorSnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    listeners.forEach((listener) => {
      listener();
    });
  };
  const clearTransaction = () => {
    try {
      storage.removeItem(PKCE_STORAGE_KEY);
    } catch {
      /* storage 차단이어도 memory 세션 폐기는 진행한다. */
    }
  };
  const persistent = options.persistentStorage;
  const readPersisted = (): string | null => {
    try {
      return persistent?.getItem(REFRESH_STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  };
  const writePersisted = (token: string) => {
    try {
      persistent?.setItem(REFRESH_STORAGE_KEY, token);
    } catch {
      /* 저장소 차단이면 이 탭 메모리 세션으로만 동작한다. */
    }
  };
  const removePersisted = () => {
    try {
      persistent?.removeItem(REFRESH_STORAGE_KEY);
    } catch {
      /* storage 차단이어도 memory 세션 폐기는 진행한다. */
    }
  };
  const reset = (
    status: Status,
    reason?: OperatorSnapshot['reason'],
    { keepPersisted = false }: { keepPersisted?: boolean } = {},
  ) => {
    controller.abort();
    controller = new AbortController();
    clearTimeout(expiryTimer);
    clearTimeout(renewTimer);
    // access 만료는 refresh 토큰까지 죽었다는 뜻이 아니다 — 새로고침으로 복구할 수 있게 남긴다.
    if (!keepPersisted && reason !== 'expired') removePersisted();
    access = null;
    identity = null;
    refreshToken = null;
    accessExpiresAt = 0;
    readRecoveryPending.clear();
    readRecoveryCandidates.clear();
    readRecoveryActive = false;
    authorizationFlight = undefined;
    authorizationFlightToken = null;
    loginFlight = undefined;
    options.clearOperationalCache();
    publish({
      status: config ? status : 'not-configured',
      generation: snapshot.generation + 1,
      authorization: null,
      deniedDomains: [],
      reason,
    });
  };
  const logout = () => {
    clearTransaction();
    reset('unauthenticated', 'logout');
  };
  const cancelLoginPreparation = () => {
    if (snapshot.status !== 'authenticating' || !loginFlight) return;
    clearTransaction();
    reset('unauthenticated', 'login-cancelled');
  };
  // 다른 탭에서 로그아웃하거나 로그인이 거부돼 저장된 토큰이 지워지면 이 탭도 로그아웃한다.
  // 토큰을 받는 시점에 한 번만 구독한다 — 로그인 전에는 저장소 이벤트가 필요 없다.
  let storageSubscribed = false;
  const watchStorage = () => {
    if (storageSubscribed || !options.subscribeStorage) return;
    storageSubscribed = true;
    options.subscribeStorage((key, newValue) => {
      if ((key === REFRESH_STORAGE_KEY || key === null) && newValue === null && access)
        reset('unauthenticated', 'logout', { keepPersisted: true });
    });
  };
  const acceptPair = (pair: TokenPair) => {
    watchStorage();
    access = pair.access_token;
    refreshToken = pair.refresh_token;
    accessExpiresAt = now() + pair.expires_in * 1000;
    writePersisted(pair.refresh_token);
    clearTimeout(expiryTimer);
    expiryTimer = setTimeout(
      () => {
        reset('unauthenticated', 'expired');
      },
      Math.min(pair.expires_in * 1000, 2_147_483_647),
    );
    scheduleRenew(pair.expires_in * 1000 * RENEW_AT);
  };
  /** 만료 전에 조용히 새 토큰으로 바꾼다 — 권한 재확인·캐시 비우기 없이 토큰만 교체해 화면이 깜빡이지 않는다. */
  const scheduleRenew = (delay: number) => {
    clearTimeout(renewTimer);
    if (!persistent) return;
    renewTimer = setTimeout(
      () => {
        void renewAccess();
      },
      Math.min(delay, 2_147_483_647),
    );
  };
  const withLock = <T>(task: () => Promise<T>): Promise<T> =>
    options.withRefreshLock ? options.withRefreshLock(task) : task();
  /** 잠금 안에서 저장된 최신 refresh 토큰으로 교환한다 — 다른 탭이 이미 회전시켰다면 그 값을 쓴다. */
  const exchangeStored = (generation: number, signal: AbortSignal): Promise<TokenPair> =>
    withLock(async () => {
      const token = persistent ? readPersisted() : refreshToken;
      if (!token) throw new TokenRejectedError('저장된 로그인이 없습니다.');
      const pair = await exchange(
        { grant_type: 'refresh_token', refresh_token: token, client_id: config?.clientId ?? '' },
        signal,
      );
      // 잠금을 풀기 전에 회전된 토큰을 저장해야 다음 탭이 새 토큰을 읽는다.
      if (generation === snapshot.generation) writePersisted(pair.refresh_token);
      return pair;
    });
  /** 읽지 못하면 null이다(실패를 "다른 계정"으로도 "같은 계정"으로도 단정하지 않는다). */
  const readIdentity = async (token: string, signal: AbortSignal): Promise<string | null> => {
    try {
      const value = await options.loadIdentity(token, signal);
      return typeof value === 'string' && value ? value : null;
    } catch {
      return null;
    }
  };
  const renewAccess = async (): Promise<void> => {
    if (!config || !refreshToken) return;
    const generation = snapshot.generation;
    const signal = controller.signal;
    try {
      const pair = await exchangeStored(generation, signal);
      if (generation !== snapshot.generation) return;
      // 조용한 갱신은 권한도 cache도 다시 보지 않으므로, 새 토큰이 지금 계정의 것임을 먼저 증명해야 한다.
      // (다른 탭이 저장소의 refresh 토큰을 다른 계정 것으로 바꿨다면 여기서 그 계정의 토큰이 나온다.)
      const next = await readIdentity(pair.access_token, signal);
      if (generation !== snapshot.generation) return;
      if (identity === null) {
        // 지금 계정을 보증할 수 없다 — 조용히 이어 가지 않고 엄격한 재확인(cache 비움)으로 신원을 다시 세운다.
        acceptPair(pair);
        await loadCurrentAuthorization();
        return;
      }
      if (next === null) {
        // 새 토큰의 주인을 확인하지 못했다. 받아들이지 않고(옛 토큰은 만료까지 그대로) 나중에 다시 시도한다.
        scheduleRenew(RENEW_RETRY_MS);
        return;
      }
      if (next !== identity) {
        // 다른 계정의 토큰이다 — 이 탭의 화면·cache·초안을 이어 쓰지 않는다. 저장소 토큰은 지우지 않는다(그 계정 것).
        reset('unauthenticated', 'expired');
        return;
      }
      access = pair.access_token;
      refreshToken = pair.refresh_token;
      accessExpiresAt = now() + pair.expires_in * 1000;
      clearTimeout(expiryTimer);
      expiryTimer = setTimeout(
        () => {
          reset('unauthenticated', 'expired');
        },
        Math.min(pair.expires_in * 1000, 2_147_483_647),
      );
      scheduleRenew(pair.expires_in * 1000 * RENEW_AT);
    } catch (error) {
      if (generation !== snapshot.generation) return;
      if (error instanceof TokenRejectedError) reset('unauthenticated', 'failed-refresh');
      // 일시 실패는 만료 전까지 다시 시도한다. 끝내 못 받으면 만료 타이머가 정리한다.
      else scheduleRenew(RENEW_RETRY_MS);
    }
  };
  const exchange = async (
    body: Record<string, string>,
    signal: AbortSignal,
  ): Promise<TokenPair> => {
    if (!config) throw new Error('운영자 인증 미연동');
    const response = await options.fetch(config.tokenUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal,
      redirect: 'error',
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
    });
    if (!response.ok) {
      if ([400, 401, 403].includes(response.status))
        throw new TokenRejectedError('인증 요청을 완료하지 못했습니다.');
      throw new Error('인증 요청을 완료하지 못했습니다.');
    }
    const value: unknown = await response.json();
    const pair = value as Partial<TokenPair> | null;
    if (
      !pair ||
      typeof pair.access_token !== 'string' ||
      !pair.access_token ||
      typeof pair.refresh_token !== 'string' ||
      !pair.refresh_token ||
      pair.token_type !== 'Bearer' ||
      typeof pair.expires_in !== 'number' ||
      !Number.isSafeInteger(pair.expires_in) ||
      pair.expires_in <= 0
    ) {
      throw new Error('인증 응답을 확인할 수 없습니다.');
    }
    return pair as TokenPair;
  };
  /**
   * 옛 토큰으로 시작한 권한 확인이 끝났는데 그사이 같은 세션(generation)에서 토큰이 바뀐 경우의 인계.
   * 세션이 그대로이고 아직 '확인 중'이며 지금 토큰이 있을 때만 지금 토큰으로 한 번 이어 확인한다.
   * 새 토큰은 그 토큰을 바꾼 쪽(갱신·교환)이 책임지는 게 원칙이지만, 조용한 갱신은 확인을 새로 열지 않으므로 여기서 이어받는다.
   * 로그아웃·계정 변경·거부로 세션(generation)이 바뀌었다면 아무것도 하지 않는다(재진입 없음).
   */
  const continueForCurrentToken = (generation: number): Promise<void> => {
    if (snapshot.generation !== generation || snapshot.status !== 'checking' || !config || !access)
      return Promise.resolve();
    return loadCurrentAuthorization();
  };
  const loadCurrentAuthorization = (): Promise<void> => {
    if (access && now() >= accessExpiresAt) {
      reset('unauthenticated', 'expired');
      return Promise.resolve();
    }
    // 합류는 같은 토큰으로 시작한 확인에만 한다. 토큰이 바뀌었는데 옛 토큰의 결과(옛 계정의 권한·신원)를 받아 쓰면 안 된다.
    if (authorizationFlight && authorizationFlightToken === access) return authorizationFlight;
    if (!config || !access) return Promise.resolve();
    const generation = snapshot.generation;
    const token = access;
    options.clearOperationalCache();
    publish({ status: 'checking', authorization: null });
    const flight = (async () => {
      try {
        const signal = controller.signal;
        const [authorization, nextIdentity] = await Promise.all([
          options.loadAuthorization(token, signal),
          readIdentity(token, signal),
        ]);
        if (snapshot.generation !== generation) return;
        // 확인하는 사이 토큰이 바뀌었으면 이 결과는 옛 토큰의 것이라 버린다. 화면이 '확인 중'에 멈추지 않도록
        // 지금 토큰으로 확인을 이어 간다(같은 토큰의 확인이 이미 있으면 거기에 합류한다).
        if (access !== token) {
          await continueForCurrentToken(generation);
          return;
        }
        // 알던 계정과 다른 계정으로 확인되면 이어 쓰지 않는다.
        if (identity !== null && nextIdentity !== null && identity !== nextIdentity) {
          reset('unauthenticated', 'expired');
          return;
        }
        // 알던 계정이 있는데 이번 토큰의 주인을 읽지 못했다면 같은 계정이라고 단정할 수 없다. authorized로 두지 않고
        // 알던 신원을 그대로 둔 채 확인 실패로 닫는다(다시 확인하면 그때 대조한다). 처음 로그인(알던 신원 없음)은 그대로 진행한다.
        if (identity !== null && nextIdentity === null) {
          handleAuthorizationFailure(new Error('토큰의 주인을 확인하지 못했습니다.'), generation);
          return;
        }
        if (!canReadManagementWorkspace(authorization)) {
          reportAccessDenied(generation);
          return;
        }
        identity = nextIdentity;
        publish({ authorization, deniedDomains: [], status: 'authorized' });
      } catch (error) {
        // 토큰이 이미 바뀌었다면 옛 토큰의 실패(예: 늦게 온 401)로 새 토큰 세션을 닫지 않는다.
        if (snapshot.generation === generation && access !== token) {
          await continueForCurrentToken(generation);
          return;
        }
        handleAuthorizationFailure(error, generation);
      }
    })();
    authorizationFlight = flight;
    authorizationFlightToken = token;
    void flight.finally(() => {
      if (authorizationFlight === flight) {
        authorizationFlight = undefined;
        authorizationFlightToken = null;
      }
    });
    return flight;
  };
  const handleAuthorizationFailure = (error: unknown, generation: number) => {
    if (snapshot.generation !== generation) return;
    if (error instanceof ApiError && error.status === 401) reset('unauthenticated', 'expired');
    else {
      options.clearOperationalCache();
      publish({
        status: error instanceof ApiError && error.status === 403 ? 'forbidden' : 'service-failure',
        generation: snapshot.generation + 1,
        authorization: null,
      });
    }
  };
  /**
   * 토큰을 새로 교환한 뒤의 권한 재확인. **계정이 같다고 이미 증명된 경우에만** 부른다.
   * 권한이 그대로면 화면을 '확인 중'으로 바꾸거나 업무 cache를 비우지 않아 열려 있던 화면과 저장하지 않은 입력이 남는다.
   * 권한이 달라졌거나 도메인 제한이 있었거나 확인에 실패하면 엄격한 경로(확인 중 표시·cache 비움·거부/실패 처리)를 탄다.
   */
  const recheckAuthorizationKeepingScreen = async (): Promise<void> => {
    const before = snapshot.authorization;
    if (
      authorizationFlight ||
      !config ||
      !access ||
      snapshot.status !== 'authorized' ||
      !before ||
      snapshot.deniedDomains.length > 0
    )
      return loadCurrentAuthorization();
    const generation = snapshot.generation;
    let unchanged = false;
    try {
      const next = await options.loadAuthorization(access, controller.signal);
      if (snapshot.generation !== generation) return;
      unchanged =
        canReadManagementWorkspace(next) && JSON.stringify(next) === JSON.stringify(before);
    } catch (error) {
      handleAuthorizationFailure(error, generation);
      return;
    }
    if (!unchanged) await loadCurrentAuthorization();
  };
  const refresh = (): Promise<boolean> => {
    if (!config || !refreshToken) return Promise.resolve(false);
    const generation = snapshot.generation;
    if (refreshFlight?.generation === generation) return refreshFlight.promise;
    const signal = controller.signal;
    const promise = (async () => {
      try {
        const pair = await exchangeStored(generation, signal);
        if (generation !== snapshot.generation) return false;
        // 새 토큰이 지금 계정의 것인지 먼저 확인한다. 다른 탭이 저장소 토큰을 다른 계정 것으로 바꿨을 수 있다.
        const next = await readIdentity(pair.access_token, signal);
        if (generation !== snapshot.generation) return false;
        if (identity !== null && next !== null && next !== identity) {
          reset('unauthenticated', 'expired');
          return false;
        }
        const sameAccount = identity !== null && next === identity;
        readRecoveryPending = new Set(readRecoveryCandidates);
        readRecoveryCandidates.clear();
        readRecoveryActive = true;
        acceptPair(pair);
        // 같은 계정임이 증명됐을 때만 화면을 유지한다. 증명하지 못했으면 엄격한 재확인(cache 비움·신원 다시 세움)을 탄다.
        if (sameAccount) await recheckAuthorizationKeepingScreen();
        else await loadCurrentAuthorization();
        return generation === snapshot.generation && snapshot.status === 'authorized';
      } catch (error) {
        if (generation === snapshot.generation)
          reset('unauthenticated', 'failed-refresh', {
            keepPersisted: !(error instanceof TokenRejectedError),
          });
        return false;
      }
    })();
    refreshFlight = { generation, promise };
    void promise.finally(() => {
      if (refreshFlight?.promise === promise) refreshFlight = undefined;
    });
    return promise;
  };
  const reportAccessDenied = (generation: number) => {
    if (generation !== snapshot.generation) return;
    controller.abort();
    controller = new AbortController();
    authorizationFlight = undefined;
    authorizationFlightToken = null;
    options.clearOperationalCache();
    publish({
      status: 'forbidden',
      generation: snapshot.generation + 1,
      authorization: null,
      deniedDomains: [],
    });
  };
  const reportDomainAccessDenied = (domain: string, generation: number) => {
    if (skipAccessGuard) return;
    if (generation !== snapshot.generation || snapshot.status !== 'authorized') return;
    if (!snapshot.deniedDomains.includes(domain)) {
      options.clearDomainOperationalCache?.(domain, generation);
      publish({ deniedDomains: [...snapshot.deniedDomains, domain] });
    }
  };
  const tokens: TokenSource = {
    getToken: () => {
      if (access && now() >= accessExpiresAt) reset('unauthenticated', 'expired');
      return access;
    },
    getGeneration: () => snapshot.generation,
    refresh,
    onUnauthorized: () => {
      reset('unauthenticated', 'expired');
    },
    onReadUnauthorized: (key) => {
      const pending = readRecoveryPending.has(key);
      if (!pending) {
        readRecoveryCandidates.add(key);
        if (readRecoveryActive) readRecoveryPending.add(key);
      }
      return pending;
    },
    onReadSuccess: (key) => {
      readRecoveryCandidates.delete(key);
      readRecoveryPending.delete(key);
      if (readRecoveryActive && readRecoveryPending.size === 0) readRecoveryActive = false;
    },
  };
  const beginLogin = (returnTo: string, origin: string): Promise<string> => {
    if (loginFlight) return loginFlight;
    const flight = (async () => {
      if (!config) throw new Error('운영자 인증 미연동');
      let safeReturn: string;
      try {
        safeReturn = safeReturnTo(returnTo);
      } catch {
        reset('unauthenticated', 'login-start-failed');
        throw new Error('로그인을 시작하지 못했습니다.');
      }
      if (new URL(config.callbackUri).origin !== origin) {
        reset('unauthenticated', 'login-start-failed');
        throw new Error('등록된 callback origin에서 로그인해 주세요.');
      }
      clearTransaction();
      reset('authenticating');
      callback = undefined;
      const generation = snapshot.generation;
      try {
        const verifier = random();
        const nonce = random();
        // 프리뷰는 등록 callback을 거쳐 돌아오므로 state에 자기 origin을 실어 중계 대상을 알린다.
        const state =
          config.callbackUri === config.redirectUri ? nonce : encodeRelayState(nonce, origin);
        const proof = await challenge(verifier);
        if (snapshot.generation !== generation) throw new Error('취소된 로그인입니다.');
        storage.setItem(
          PKCE_STORAGE_KEY,
          JSON.stringify({
            state,
            verifier,
            redirectUri: config.redirectUri,
            returnTo: safeReturn,
            expiresAt: now() + TRANSACTION_TTL,
          } satisfies Transaction),
        );
        const url = new URL(config.authorizeUrl);
        Object.entries({
          client_id: config.clientId,
          audience: config.audience,
          redirect_uri: config.redirectUri,
          state,
          code_challenge: proof,
          response_type: 'code',
          code_challenge_method: 'S256',
        }).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
        return url.href;
      } catch {
        if (generation === snapshot.generation) reset('unauthenticated', 'login-start-failed');
        throw new Error('로그인을 시작하지 못했습니다.');
      }
    })();
    loginFlight = flight;
    void flight.then(
      () => {
        if (loginFlight === flight) loginFlight = undefined;
      },
      () => {
        if (loginFlight === flight) loginFlight = undefined;
      },
    );
    return flight;
  };
  const completeCallback = (href: string, cleanBrowserUrl: () => void): Promise<string> => {
    // 비동기 처리·렌더보다 먼저 제거한다. 이 URL이나 예외 원문을 로그로 보내지 않는다.
    cleanBrowserUrl();
    if (callback?.identity === href) return callback.promise;
    const generation = snapshot.generation;
    const promise = (async () => {
      try {
        if (!config) throw new Error('운영자 인증 미연동');
        const url = new URL(href);
        const raw = storage.getItem(PKCE_STORAGE_KEY);
        storage.removeItem(PKCE_STORAGE_KEY); // 검증 실패도 소비한다. concurrent/StrictMode 재사용은 위 promise만 공유한다.
        const tx = (raw ? JSON.parse(raw) : null) as Partial<Transaction> | null;
        const singleton = (key: string) =>
          url.searchParams.getAll(key).length === 1 ? url.searchParams.get(key) : null;
        const code = singleton('code');
        const state = singleton('state');
        if (
          url.origin + url.pathname !== config.callbackUri ||
          url.hash ||
          tx?.redirectUri !== config.redirectUri ||
          typeof tx.state !== 'string' ||
          tx.state !== state ||
          typeof tx.verifier !== 'string' ||
          !/^[A-Za-z0-9_-]{43,128}$/.test(tx.verifier) ||
          typeof tx.expiresAt !== 'number' ||
          tx.expiresAt <= now() ||
          tx.expiresAt > now() + TRANSACTION_TTL ||
          typeof tx.returnTo !== 'string' ||
          !code ||
          url.searchParams.has('error')
        )
          throw new Error('잘못된 callback입니다.');
        const returnTo = safeReturnTo(tx.returnTo);
        const pair = await exchange(
          {
            grant_type: 'authorization_code',
            code,
            code_verifier: tx.verifier,
            client_id: config.clientId,
            redirect_uri: config.redirectUri,
            audience: config.audience,
          },
          controller.signal,
        );
        if (generation !== snapshot.generation) return '/properties';
        options.clearOperationalCache();
        await options.resolveMember?.(pair.access_token, controller.signal);
        if (generation !== snapshot.generation) return '/properties';
        acceptPair(pair);
        await loadCurrentAuthorization();
        return returnTo;
      } catch {
        if (generation === snapshot.generation) reset('unauthenticated', 'invalid-callback');
        throw new Error('로그인 응답을 확인하지 못했습니다. 다시 로그인해 주세요.');
      }
    })();
    callback = { identity: href, promise };
    return promise;
  };
  /**
   * 새로고침·새 탭·새 창에서 메모리 세션이 비어 있을 때 저장된 refresh 토큰으로 로그인을 이어 받는다.
   * 성공하면 true. 저장된 토큰이 없거나 거부·일시 실패면 false — 호출자가 로그인을 시작한다(상태는 그대로 둔다).
   */
  const restore = (): Promise<boolean> => {
    if (restoreFlight) return restoreFlight;
    if (!config || !persistent || access || !readPersisted()) return Promise.resolve(false);
    const generation = snapshot.generation;
    const flight = (async () => {
      try {
        const pair = await exchangeStored(generation, controller.signal);
        if (generation !== snapshot.generation) return false;
        acceptPair(pair);
        await loadCurrentAuthorization();
        return access !== null;
      } catch (error) {
        if (error instanceof TokenRejectedError) removePersisted();
        return false;
      }
    })();
    restoreFlight = flight;
    void flight.finally(() => {
      if (restoreFlight === flight) restoreFlight = undefined;
    });
    return flight;
  };
  return {
    tokens,
    restore,
    beginLogin,
    cancelLoginPreparation,
    completeCallback,
    logout,
    loadCurrentAuthorization,
    reportAccessDenied,
    reportDomainAccessDenied,
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
export type OperatorSession = ReturnType<typeof createOperatorSession>;
